import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { KTIcon } from '../../../../../../_metronic/helpers'

type Mode = 'hover' | 'click'

// Panel padding/border. The panel is offset back by these so its content origin
// lands exactly on the collapsed content's origin — the already-visible first
// chips stay put on expand instead of jumping inward by the panel padding.
const PANEL_PAD_X = 10
const PANEL_PAD_Y = 8
const PANEL_BORDER = 1

interface Props {
  children: React.ReactNode
  // 'hover' opens on mouse-over, 'click' toggles on click and closes on outside click.
  mode?: Mode
  // collapsed clamp height in px (~2 chip rows by default)
  collapsedHeight?: number
  // floating panel never narrower than this
  minPanelWidth?: number
}

// CollapsibleCell clamps its content to `collapsedHeight`; when the content
// overflows it fades the bottom edge and shows a chevron affordance. Expanding
// (on hover or click, per `mode`) reveals the full content in a floating panel
// portaled to <body> — so it is never clipped by the table's overflow container —
// with a solid background and a downward shadow, so it reads as lifted above the
// table instead of blending into it. When content fits, it renders inertly.
export const CollapsibleCell: React.FC<Props> = ({
  children,
  mode = 'hover',
  collapsedHeight = 49,
  minPanelWidth = 280,
}) => {
  const anchorRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const closeTimer = useRef<number | undefined>(undefined)

  const [measured, setMeasured] = useState(false)
  const [overflowing, setOverflowing] = useState(false)
  const [open, setOpen] = useState(false)
  const [rect, setRect] = useState<{ top: number; left: number; width: number } | null>(null)
  // The bottom fade must blend into the actual (striped) row colour, not a fixed
  // surface — so we read the nearest opaque ancestor background at measure time.
  const [fadeColor, setFadeColor] = useState('var(--bs-body-bg, #fff)')

  useLayoutEffect(() => {
    const el = measureRef.current
    if (!el) return
    setOverflowing(el.scrollHeight > collapsedHeight + 2)
    setMeasured(true)

    let node: HTMLElement | null = anchorRef.current
    while (node) {
      const bg = getComputedStyle(node).backgroundColor
      if (bg && bg !== 'transparent' && !/,\s*0\s*\)$/.test(bg)) {
        setFadeColor(bg)
        break
      }
      node = node.parentElement
    }
  }, [children, collapsedHeight])

  // Clamp until we've confirmed the content fits — so the row is never briefly
  // rendered at full height on first paint (which would make it jump on load).
  const clamped = !measured || overflowing

  const computeRect = useCallback(() => {
    const el = anchorRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    setRect({ top: r.top, left: r.left, width: r.width })
  }, [])

  const openNow = useCallback(() => {
    if (!overflowing) return
    window.clearTimeout(closeTimer.current)
    computeRect()
    setOpen(true)
  }, [overflowing, computeRect])

  const scheduleClose = useCallback(() => {
    window.clearTimeout(closeTimer.current)
    closeTimer.current = window.setTimeout(() => setOpen(false), 90)
  }, [])

  const closeNow = useCallback(() => {
    window.clearTimeout(closeTimer.current)
    setOpen(false)
  }, [])

  // Keep the floating panel anchored to the cell while scrolling/resizing.
  useEffect(() => {
    if (!open) return
    const onMove = () => computeRect()
    window.addEventListener('scroll', onMove, true)
    window.addEventListener('resize', onMove)
    return () => {
      window.removeEventListener('scroll', onMove, true)
      window.removeEventListener('resize', onMove)
    }
  }, [open, computeRect])

  // Click mode: close when clicking outside both the anchor and the panel.
  useEffect(() => {
    if (mode !== 'click' || !open) return
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (anchorRef.current?.contains(t) || panelRef.current?.contains(t)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [mode, open])

  useEffect(() => () => window.clearTimeout(closeTimer.current), [])

  const hoverHandlers =
    mode === 'hover' && overflowing ? { onMouseEnter: openNow, onMouseLeave: scheduleClose } : {}
  const clickHandlers =
    mode === 'click' && overflowing
      ? { onClick: () => (open ? closeNow() : openNow()), style: { cursor: 'pointer' as const } }
      : {}

  return (
    <div ref={anchorRef} style={{ position: 'relative' }} {...hoverHandlers} {...clickHandlers}>
      {/* In-flow content stays clamped even while open, so the row never grows;
          the full content lives in the floating panel instead. */}
      <div ref={measureRef} style={clamped ? { maxHeight: collapsedHeight, overflow: 'hidden' } : undefined}>
        {children}
      </div>

      {overflowing && !open && (
        <>
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              height: '60%',
              pointerEvents: 'none',
              background: `linear-gradient(to bottom, transparent, ${fadeColor})`,
            }}
          />
          <span
            className='d-inline-flex align-items-center justify-content-center'
            style={{
              position: 'absolute',
              right: 2,
              bottom: 2,
              width: 18,
              height: 18,
              borderRadius: 6,
              background: 'var(--bs-body-bg)',
              boxShadow: '0 0 0 1px var(--bs-border-color)',
              color: 'var(--bs-gray-600)',
            }}
          >
            <KTIcon iconName='arrow-down' className='fs-8' />
          </span>
        </>
      )}

      {open &&
        rect &&
        createPortal(
          <div
            ref={panelRef}
            onMouseEnter={mode === 'hover' ? openNow : undefined}
            onMouseLeave={mode === 'hover' ? scheduleClose : undefined}
            style={{
              position: 'fixed',
              // Offset back by padding+border (box-sizing: border-box) so the
              // content box, not the border box, aligns with the collapsed cell.
              top: rect.top - PANEL_PAD_Y - PANEL_BORDER,
              left: rect.left - PANEL_PAD_X - PANEL_BORDER,
              width: Math.max(rect.width, minPanelWidth) + 2 * (PANEL_PAD_X + PANEL_BORDER),
              zIndex: 1080,
              // --bs-card-bg is only defined inside .card; the panel is portaled
              // to <body>, so use the root-level surface var (with a solid
              // fallback) to guarantee an opaque background.
              background: 'var(--bs-body-bg, #fff)',
              border: `${PANEL_BORDER}px solid var(--bs-border-color)`,
              borderRadius: 8,
              boxShadow: '0 14px 34px -6px rgba(0, 0, 0, 0.32)',
              padding: `${PANEL_PAD_Y}px ${PANEL_PAD_X}px`,
              maxHeight: 340,
              overflowY: 'auto',
            }}
          >
            {children}
          </div>,
          document.body
        )}
    </div>
  )
}
